'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useId, useRef, useState } from 'react';
import { PRIMARY_CTA, PRIMARY_NAV } from '@/lib/site';
import { buttonClass } from '@/components/ui/Button';

/**
 * The site header: the same navigation on every page, so customers, operators
 * and people looking for help find their way from anywhere.
 *
 * On small screens the links collapse into a disclosure (not a modal): the
 * toggle reports its state, Escape closes it and returns focus to the toggle,
 * and following a link closes it. Without JavaScript the toggle does nothing,
 * so the footer repeats every destination.
 */
export default function SiteHeader() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  // A client-side navigation lands on a new page; the menu should not follow it there.
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const isCurrent = (href: string) => (href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`));

  return (
    // Solid ink: a translucent header over the light sections read as a grey bar that matched nothing below it.
    <header className="sticky top-0 z-40 border-b border-white/10 bg-ink-950 text-on-ink">
      <div className="container-page flex h-16 items-center justify-between gap-6 lg:h-[72px]">
        <Link href="/" className="-m-1 shrink-0 rounded-md p-1 focus-ring-on-ink">
          <Image src="/assets/logo-text.png" alt="Point2 home" width={115} height={30} loading="eager" className="h-[30px] w-[115px]" />
        </Link>

        <nav aria-label="Main" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {PRIMARY_NAV.map((item) => {
              const current = isCurrent(item.href);
              return (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    aria-current={current ? 'page' : undefined}
                    className={`inline-flex min-h-11 items-center rounded-lg px-3.5 text-[15px] font-medium transition-colors focus-ring-on-ink ${
                      current ? 'text-white' : 'text-on-ink-muted hover:text-white'
                    }`}
                  >
                    <span className={current ? 'border-b-2 border-brand-400 pb-0.5' : 'pb-0.5'}>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex items-center gap-2">
          {/* Wrapped: `hidden` on the link itself would lose to the button's own `inline-flex`. */}
          <div className="hidden min-[400px]:block">
            <Link href={PRIMARY_CTA.href} className={`${buttonClass('primary')} focus-ring-on-ink`}>
              {PRIMARY_CTA.label}
            </Link>
          </div>
          <button
            ref={toggleRef}
            type="button"
            className="inline-flex size-11 items-center justify-center rounded-lg text-on-ink hover:bg-white/10 focus-ring-on-ink lg:hidden"
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="sr-only">Menu</span>
            <svg aria-hidden="true" viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      <nav id={menuId} aria-label="Main" hidden={!open} className="border-t border-white/10 bg-ink-950 lg:hidden">
        <ul className="container-page flex flex-col py-3">
          {PRIMARY_NAV.map((item) => {
            const current = isCurrent(item.href);
            return (
              <li key={item.href}>
                <Link
                  href={item.href}
                  aria-current={current ? 'page' : undefined}
                  onClick={() => setOpen(false)}
                  className={`flex min-h-12 items-center rounded-lg px-3 text-base font-medium focus-ring-on-ink ${
                    current ? 'bg-white/10 text-white' : 'text-on-ink-muted hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
          <li className="pt-3 pb-2 min-[400px]:hidden">
            <Link href={PRIMARY_CTA.href} onClick={() => setOpen(false)} className={`${buttonClass('primary', 'lg')} w-full focus-ring-on-ink`}>
              {PRIMARY_CTA.label}
            </Link>
          </li>
        </ul>
      </nav>
    </header>
  );
}
